import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import LanguageToggle from "./LanguageToggle";
import { useAuth } from "../context/AuthContext";

// `season` (optional) dresses the header for a seasonal theme.
export default function Header({ season = null }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const [scrolled, setScrolled] = useState(false);

  useEffect(() => setOpen(false), [location.pathname]);

  // While the phone menu is open the page behind it stays put, and Escape
  // or a tap outside closes it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.classList.add("menu-open");
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("menu-open");
    };
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header
      className={`header${scrolled ? " is-scrolled" : ""}${season ? " header--season" : ""}`}
      style={
        season
          ? {
              "--season-from": season.colors.from,
              "--season-to": season.colors.to,
            }
          : undefined
      }
    >
      <div className="container header__inner">
        <Link to={user ? "/dashboard" : "/"} className="header__brand">
          <Logo />
          {season && (
            <span className="header__season" aria-hidden="true">
              {season.emoji}
            </span>
          )}
        </Link>

        <button
          type="button"
          className={`header__burger${open ? " is-open" : ""}`}
          aria-expanded={open}
          aria-label={t("nav.menu")}
          onClick={() => setOpen((value) => !value)}
        >
          <span />
          <span />
          <span />
        </button>

        {open && (
          <div
            className="header__backdrop"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
        )}

        <nav className={`header__nav${open ? " is-open" : ""}`}>
          <NavLink to="/templates" className="header__link">
            {t("nav.templates")}
          </NavLink>
          {user ? (
            <>
              <NavLink to="/dashboard" className="header__link">
                {t("nav.dashboard")}
              </NavLink>
              <NavLink to="/account" className="header__link">
                {t("nav.account")}
              </NavLink>
              <button
                type="button"
                className="header__link header__link--btn"
                onClick={handleLogout}
              >
                {t("nav.logout")}
              </button>
              <LanguageToggle />
              <Link to="/create" className="btn btn--gradient btn--sm">
                {t("nav.create")}
              </Link>
            </>
          ) : (
            <>
              <NavLink to="/login" className="header__link">
                {t("nav.login")}
              </NavLink>
              <LanguageToggle />
              <Link to="/register" className="btn btn--gradient btn--sm">
                {t("nav.signup")}
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
